import type { CoreExpression, Identifier, Literal } from 'jsep';
import jsep from 'jsep';
import { ActivityType, activityTypes } from '../model/activity';
import { Property } from '../model/entity-type';
import { ExpressionValidationResult } from '../model/expression';
import { BasicProperty, standardProperties } from './activities-eval';
import {
  ExpressionReturnType,
  equivalentTypes,
  printExpression
} from './common';

/*
in-use:
Identifier
Literal
CallExpression
BinaryExpression
UnaryExpression (only negation of literal numbers and boolean expressions)
*/

const functions: Record<
  string,
  {
    returnType: (args: {
      context: ActivityExpressionValidationContext;
      args: CoreExpression[];
      valueTypes: ExpressionReturnType[];
    }) => ExpressionReturnType;
    minArguments: number;
    maxArguments: number;
    restArgument: boolean;
    arguments: (
      | {
          type: 'property-reference';
          validate: (
            property: BasicProperty
          ) => { valid: true } | { valid: false; reason: string };
        }
      | {
          type: 'choice-reference';
        }
      | {
          type: 'literal';
          validate: (
            value: Literal['value']
          ) => { valid: true } | { valid: false; reason: string };
        }
      | {
          type: 'value';
          validate: (
            type: ExpressionReturnType,
            valueTypes: ExpressionReturnType[],
            index: number,
            length: number
          ) => { valid: true } | { valid: false; reason: string };
        }
      | {
          type: 'contextual-identifier';
          validate: (
            name: string
          ) => { valid: true } | { valid: false; reason: string };
        }
    )[];
  }
> = {
  SELECTED: {
    returnType: () => 'boolean',
    minArguments: 2,
    maxArguments: 33,
    restArgument: true,
    arguments: [
      {
        type: 'property-reference',
        validate: property =>
          ['single', 'multiple'].includes(property.choice_type)
            ? { valid: true }
            : {
                valid: false,
                reason: `Property ${property.property_id} is not a single or multiple choice property.`
              }
      },
      { type: 'choice-reference' }
    ]
  },
  COUNTIF: {
    returnType: () => 'int',
    minArguments: 1,
    maxArguments: 15,
    restArgument: true,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'boolean'
            ? { valid: true }
            : { valid: false, reason: `Expected boolean.` }
      }
    ]
  },
  ANY: {
    returnType: () => 'boolean',
    minArguments: 2,
    maxArguments: 15,
    restArgument: true,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'boolean'
            ? { valid: true }
            : { valid: false, reason: `Expected boolean.` }
      },
      {
        type: 'value',
        validate: type =>
          type === 'boolean'
            ? { valid: true }
            : { valid: false, reason: `Expected boolean.` }
      }
    ]
  },
  VALUE: {
    returnType: ({ context: { properties }, args }) => {
      const node = args[0] as Literal | Identifier;
      let property: BasicProperty | undefined = undefined;
      if (node.type === 'Literal') {
        property = properties[node.value as number];
      } else {
        property = standardProperties[node.name.toUpperCase()];
      }
      if (!property) {
        throw new Error(
          `Unable to locate property for "${printExpression(node)}".`
        );
      }
      switch (property?.type) {
        case 'signature':
        case 'medications':
        case 'attachment':
        case 'int':
          return 'int';
        case 'currency':
        case 'decimal':
          return 'decimal';
        case 'bit':
          return 'boolean';
        case 'varchar100':
        case 'varcharmax':
          return 'string';
        case 'date':
          return 'date';
        default:
          throw new Error(
            `Unexepected property type "${property?.type}" used in "VALUE(${node.value})" expression.`
          );
      }
    },
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'property-reference',
        validate: property =>
          ['int', 'decimal', 'currency'].includes(property.type)
            ? { valid: true }
            : {
                valid: false,
                reason: `Property ${property.property_id} is not an int, decimal, or currency property.`
              }
      }
    ]
  },
  ANSWERED: {
    returnType: () => 'boolean',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      { type: 'property-reference', validate: () => ({ valid: true }) }
    ]
  },
  SCORE: {
    returnType: () => 'int',
    minArguments: 1,
    maxArguments: 32,
    restArgument: true,
    arguments: [
      {
        type: 'property-reference',
        validate: property =>
          ['single', 'multiple'].includes(property.choice_type)
            ? { valid: true }
            : {
                valid: false,
                reason: `Property ${property.property_id} is not a single or multiple choice property.`
              }
      },
      { type: 'choice-reference' }
    ]
  },
  ABS: {
    returnType: ({ valueTypes: [type] }) => type,
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'decimal' || type === 'int'
            ? { valid: true }
            : { valid: false, reason: `Expected number` }
      }
    ]
  },
  ROUND: {
    returnType: ({ args: [, len] }) =>
      !len || (len.type == 'Literal' && len.value === 0) ? 'int' : 'decimal',
    minArguments: 1,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'decimal' || type === 'int'
            ? { valid: true }
            : { valid: false, reason: `Expected number` }
      },
      {
        type: 'literal',
        validate: value =>
          typeof value === 'number' &&
          Number.isInteger(value) &&
          value >= 0 &&
          value <= 3
            ? { valid: true }
            : { valid: false, reason: `Expected integer between 0 and 3` }
      }
    ]
  },
  FLOOR: {
    returnType: () => 'int',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'decimal' || type === 'int'
            ? { valid: true }
            : { valid: false, reason: `Expected number` }
      }
    ]
  },
  CEILING: {
    returnType: () => 'int',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'decimal' || type === 'int'
            ? { valid: true }
            : { valid: false, reason: `Expected number` }
      }
    ]
  },
  MIN: {
    returnType: ({ valueTypes }) =>
      valueTypes.includes('decimal') ? 'decimal' : 'int',
    minArguments: 2,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'decimal' || type === 'int'
            ? { valid: true }
            : { valid: false, reason: `Expected number` }
      },
      {
        type: 'value',
        validate: type =>
          type === 'decimal' || type === 'int'
            ? { valid: true }
            : { valid: false, reason: `Expected number` }
      }
    ]
  },
  MAX: {
    returnType: ({ valueTypes }) =>
      valueTypes.includes('decimal') ? 'decimal' : 'int',
    minArguments: 2,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'decimal' || type === 'int'
            ? { valid: true }
            : { valid: false, reason: `Expected number` }
      },
      {
        type: 'value',
        validate: type =>
          type === 'decimal' || type === 'int'
            ? { valid: true }
            : { valid: false, reason: `Expected number` }
      }
    ]
  },
  NOW: {
    returnType: () => 'datetime',
    minArguments: 0,
    maxArguments: 0,
    restArgument: false,
    arguments: []
  },
  AGE: {
    returnType: () => 'int',
    minArguments: 2,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      },
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      }
    ]
  },
  DATEADD: {
    returnType: ({ valueTypes }) => valueTypes.at(-1)!,
    minArguments: 3,
    maxArguments: 3,
    restArgument: false,
    arguments: [
      {
        type: 'contextual-identifier',
        validate: name =>
          ['DAY', 'MONTH', 'QUARTER', 'YEAR', 'WEEK'].includes(
            name.toUpperCase()
          )
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected "DAY", "WEEK", "MONTH", "QUARTER", or "YEAR"`
              }
      },
      {
        type: 'literal',
        validate: value =>
          Number.isInteger(value)
            ? { valid: true }
            : { valid: false, reason: `Expected integer` }
      },
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      }
    ]
  },
  DATEDIFF: {
    returnType: () => 'int',
    minArguments: 3,
    maxArguments: 3,
    restArgument: false,
    arguments: [
      {
        type: 'contextual-identifier',
        validate: name =>
          ['DAY', 'MONTH', 'QUARTER', 'YEAR', 'WEEK'].includes(
            name.toUpperCase()
          )
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected "DAY", "WEEK", "MONTH", "QUARTER", or "YEAR"`
              }
      },
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      },
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      }
    ]
  },
  DATEPART: {
    returnType: () => 'int',
    minArguments: 2,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'contextual-identifier',
        validate: name =>
          ['DAY', 'MONTH', 'QUARTER', 'YEAR', 'WEEK'].includes(
            name.toUpperCase()
          )
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected "DAY", "WEEK", "MONTH", "QUARTER", or "YEAR"`
              }
      },
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      }
    ]
  },
  DATETRUNC: {
    returnType: () => 'date',
    minArguments: 2,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'contextual-identifier',
        validate: name =>
          ['DAY', 'MONTH', 'QUARTER', 'YEAR', 'WEEK'].includes(
            name.toUpperCase()
          )
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected "DAY", "WEEK", "MONTH", "QUARTER", or "YEAR"`
              }
      },
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      }
    ]
  },
  EOMONTH: {
    returnType: () => 'datetime',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      }
    ]
  },
  DATE: {
    returnType: () => 'date',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'literal',
        validate: value =>
          typeof value === 'string' &&
          isFinite(Date.parse(value)) &&
          /^\d{4}-\d{1,2}-\d{1,2}$/.test(value)
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected string in the format of "YYYY-MM-DD"`
              }
      }
    ]
  },
  IF: {
    returnType: ({ valueTypes: [, type1, type2] }) =>
      type1 === type2 ? type1 : equivalentTypes[type1][0],
    minArguments: 3,
    maxArguments: 3,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'boolean'
            ? { valid: true }
            : { valid: false, reason: `Expected boolean` }
      },
      {
        type: 'value',
        validate: () => ({ valid: true })
      },
      {
        type: 'value',
        validate: (type2, [, type1]) =>
          equivalentTypes[type1].includes(type2)
            ? { valid: true }
            : { valid: false, reason: `Expected arguments of the same type.` }
      }
    ]
  },
  LOWER: {
    returnType: () => 'string',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'string'
            ? { valid: true }
            : { valid: false, reason: `Expected string` }
      }
    ]
  },
  UPPER: {
    returnType: () => 'string',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'string'
            ? { valid: true }
            : { valid: false, reason: `Expected string` }
      }
    ]
  },
  RURAL: {
    returnType: () => 'boolean',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      }
    ]
  },
  AREA_OF_PERSISTENT_UNEMPLOYMENT: {
    returnType: () => 'boolean',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'date' || type === 'datetime'
            ? { valid: true }
            : { valid: false, reason: `Expected date` }
      }
    ]
  },
  HAS_VISIBLE_PREDECESSORS: {
    returnType: () => 'boolean',
    minArguments: 0,
    maxArguments: 0,
    restArgument: false,
    arguments: []
  },
  DEBUG: {
    returnType: ({ valueTypes }) => valueTypes.at(-1)!,
    minArguments: 2,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'literal',
        validate: value =>
          typeof value === 'string'
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected debug label to be string literal.`
              }
      },
      {
        type: 'value',
        validate: () => ({ valid: true })
      }
    ]
  },
  CAN_VIEW_ACTIVITY_TYPE: {
    returnType: () => 'boolean',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'literal',
        validate: value =>
          typeof value === 'string' &&
          activityTypes.includes(value as ActivityType)
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected activity type to be one of '${activityTypes.join(', ')}'.`
              }
      }
    ]
  },
  MID: {
    returnType: () => 'string',
    minArguments: 2,
    maxArguments: 3,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'string'
            ? { valid: true }
            : { valid: false, reason: `Expected string` }
      },
      {
        type: 'literal',
        validate: value =>
          typeof value === 'number' && Number.isInteger(value) && value > 1
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected integer greater than or equal to 1`
              }
      },
      {
        type: 'literal',
        validate: value =>
          typeof value === 'number' && Number.isInteger(value) && value >= 0
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected integer greater than or equal to 0`
              }
      }
    ]
  },
  LEFT: {
    returnType: () => 'string',
    minArguments: 2,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'string'
            ? { valid: true }
            : { valid: false, reason: `Expected string` }
      },
      {
        type: 'literal',
        validate: value =>
          typeof value === 'number' && Number.isInteger(value) && value >= 0
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected integer greater than or equal to 0`
              }
      }
    ]
  },
  RIGHT: {
    returnType: () => 'string',
    minArguments: 2,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'string'
            ? { valid: true }
            : { valid: false, reason: `Expected string` }
      },
      {
        type: 'literal',
        validate: value =>
          typeof value === 'number' && Number.isInteger(value) && value >= 0
            ? { valid: true }
            : {
                valid: false,
                reason: `Expected integer greater than or equal to 0`
              }
      }
    ]
  },
  MATCH: {
    returnType: () => 'boolean',
    minArguments: 2,
    maxArguments: 20,
    restArgument: true,
    arguments: [
      {
        type: 'value',
        validate: type =>
          type === 'string' || type === 'int' || type === 'decimal'
            ? { valid: true }
            : { valid: false, reason: `Expected string, int or decimal` }
      },
      {
        type: 'value',
        validate: (type, valueTypes) =>
          type === valueTypes[0]
            ? { valid: true }
            : { valid: false, reason: `Expected ${valueTypes[0]}` }
      }
    ]
  },
  /* logical1, value1, logical2, value2, logical3, value3 */
  SWITCH: {
    returnType: ({ valueTypes }) => valueTypes[2],
    minArguments: 5,
    maxArguments: 30,
    restArgument: true,
    arguments: [
      {
        type: 'value',
        validate: (type, valueTypes, index, length) => {
          if (index === 0 || index === 2) {
            return { valid: true };
          } else if (
            index % 2 === 0 ||
            (length % 2 === 0 && index === length - 1)
          ) {
            if (equivalentTypes[type].includes(valueTypes[2])) {
              return { valid: true };
            } else {
              return {
                valid: false,
                reason: `Expected ${valueTypes[2]} but got ${type}`
              };
            }
          } else {
            if (equivalentTypes[type].includes(valueTypes[0])) {
              return { valid: true };
            } else {
              return {
                valid: false,
                reason: `Expected ${valueTypes[0]} but got ${type}`
              };
            }
          }
        }
      }
    ]
  },
  IFS: {
    returnType: ({ valueTypes }) => valueTypes[1],
    minArguments: 2,
    maxArguments: 30,
    restArgument: true,
    arguments: [
      {
        type: 'value',
        validate: (type, valueTypes, index, length) => {
          if (index === length - 1 && length % 2 !== 0) {
            return {
              valid: false,
              reason: `Expected even number of arguments.`
            };
          }
          if (index === 1) {
            return { valid: true };
          } else if (index % 2 === 0) {
            if (type === 'boolean') {
              return { valid: true };
            } else {
              return {
                valid: false,
                reason: `Expected boolean but got ${type}`
              };
            }
          } else {
            if (equivalentTypes[type].includes(valueTypes[1])) {
              return { valid: true };
            } else {
              return {
                valid: false,
                reason: `Expected ${valueTypes[1]} but got ${type}`
              };
            }
          }
        }
      }
    ]
  },
  ANCESTOR: {
    returnType: ({ valueTypes }) => valueTypes[0],
    minArguments: 1,
    maxArguments: 2,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: () => ({ valid: true })
      },
      {
        type: 'literal',
        validate: value => {
          if (
            typeof value === 'number' &&
            Number.isInteger(value) &&
            value > 0 &&
            value < 3
          ) {
            return { valid: true };
          }
          return {
            valid: false,
            reason: `Expected integer between 1 and 2.`
          };
        }
      }
    ]
  },
  TEXT: {
    returnType: () => 'string',
    minArguments: 1,
    maxArguments: 1,
    restArgument: false,
    arguments: [
      {
        type: 'value',
        validate: type => {
          if (['boolean', 'int', 'decimal'].includes(type)) {
            return { valid: true };
          }
          return {
            valid: false,
            reason: `Expected boolean, integer, or decimal but got ${type}`
          };
        }
      }
    ]
  }
};

export interface ActivityExpressionValidationContext {
  properties: Record<number, Property>;
  references: number[];
  scanReferences?: boolean;
  identifiers: Record<string, ExpressionReturnType>;
}

function validateActivityExpression({
  node,
  context
}: {
  node: CoreExpression;
  context: ActivityExpressionValidationContext;
}): ExpressionValidationResult {
  switch (node.type) {
    case 'BinaryExpression': {
      const { left, right, operator } = node;
      const types = {
        '+': ['int', 'decimal', 'string'],
        '-': ['int', 'decimal'],
        '*': ['int', 'decimal'],
        '/': ['int', 'decimal'],
        '%': ['int', 'decimal'],
        '&&': ['boolean'],
        '||': ['boolean'],
        '>': ['int', 'decimal', 'date', 'datetime'],
        '>=': ['int', 'decimal', 'date', 'datetime'],
        '<': ['int', 'decimal', 'date', 'datetime'],
        '<=': ['int', 'decimal', 'date', 'datetime'],
        '==': ['int', 'decimal', 'date', 'datetime', 'string', 'boolean'],
        '!=': ['int', 'decimal', 'date', 'datetime', 'string', 'boolean']
      }[operator];
      if (!types) {
        return {
          valid: false,
          reason: `Invalid operator "${operator}" in expression "${printExpression(
            node
          )}".`
        };
      }
      const leftResult = validateActivityExpression({
        node: left as CoreExpression,
        context
      });
      if (leftResult.valid === false) {
        return leftResult;
      }
      if (!context.scanReferences && !types.includes(leftResult.type)) {
        return {
          valid: false,
          reason: `The expression "${printExpression(
            left as CoreExpression,
            []
          )}" (${leftResult.type}) cannot be used with the "${operator}" operator because it does not evaluate to one of the following types: ${types.join(
            ', '
          )}.`
        };
      }
      const rightResult = validateActivityExpression({
        node: right as CoreExpression,
        context
      });
      if (rightResult.valid === false) {
        return rightResult;
      }
      if (!context.scanReferences) {
        if (!types.includes(rightResult.type)) {
          return {
            valid: false,
            reason: `The expression "${printExpression(
              right as CoreExpression,
              []
            )}" (${rightResult.type}) cannot be used with the "${operator}" operator because it does not evaluate to one of the following types: ${types.join(
              ', '
            )}.`
          };
        }
        if (!equivalentTypes[leftResult.type].includes(rightResult.type)) {
          return {
            valid: false,
            reason: `The expression "${printExpression(
              right as CoreExpression,
              []
            )}" (${rightResult.type}) does not match the type of the expression on the left side of the "${operator}" operator (${equivalentTypes[
              leftResult.type
            ].join(', ')}).`
          };
        }
      }
      if (['&&', '||', '>', '>=', '<', '<=', '==', '!='].includes(operator)) {
        return { valid: true, type: 'boolean' };
      } else if (
        operator === '/' ||
        leftResult.type === 'decimal' ||
        rightResult.type === 'decimal'
      ) {
        return { valid: true, type: 'decimal' };
      } else {
        return { valid: true, type: leftResult.type };
      }
    }
    case 'CallExpression': {
      // Ensure callee is literal.
      const callee = node.callee as CoreExpression;
      if (callee.type !== 'Identifier') {
        return { valid: false, reason: `Invalid expression.` };
      }
      // Coerce callee name to uppercase.
      callee.name = callee.name.toUpperCase();
      // Ensure callee is supported function.
      const fn = functions[callee.name];
      if (!fn) {
        return { valid: false, reason: `Invalid expression "${callee.name}".` };
      }
      // Ensure minimum number of arguments.
      if (fn.minArguments > node.arguments.length) {
        return {
          valid: false,
          reason: `Too few arguments for "${callee.name}" function. Expected ${
            fn.minArguments === fn.arguments.length
              ? fn.minArguments
              : fn.minArguments > fn.arguments.length
                ? `at least ${fn.minArguments}`
                : `${fn.minArguments} - ${fn.arguments.length}`
          }.`
        };
      }
      // Ensure maximum number of arguments.
      if (fn.maxArguments < node.arguments.length) {
        return {
          valid: false,
          reason: `Too many arguments for "${callee.name}" function. Expected at most ${fn.maxArguments} arguments.`
        };
      }
      // Validate each argument.
      let property: BasicProperty | undefined = undefined;
      const valueTypes: ExpressionReturnType[] = [];
      for (let i = 0; i < node.arguments.length; i++) {
        const argInfo =
          i < fn.arguments.length
            ? fn.arguments[i]
            : fn.restArgument
              ? fn.arguments[fn.arguments.length - 1]
              : null;
        if (!argInfo) {
          throw new Error(
            `Invalid function definition. Unable to find argument definition for argument ${i} of ${callee.name}. Check maxArguments and restArgument properties.`
          );
        }
        const argNode = node.arguments[i] as CoreExpression;
        switch (argInfo.type) {
          case 'property-reference': {
            if (
              argNode.type === 'Identifier' &&
              Object.hasOwn(standardProperties, argNode.name.toUpperCase())
            ) {
              property = standardProperties[argNode.name.toUpperCase()];
            } else if (
              argNode.type === 'Literal' &&
              typeof argNode.value === 'number' &&
              Number.isInteger(argNode.value)
            ) {
              property = context.properties[argNode.value];
              if (!context.references.includes(argNode.value)) {
                context.references.push(argNode.value);
              }
            } else {
              return {
                valid: false,
                reason: `Invalid argument "${
                  argNode.raw ?? argNode.name
                }" in "${printExpression(
                  node
                )}". Expected an integer property id or one of the following: ${Object.getOwnPropertyNames(standardProperties).join(', ')}.`
              };
            }
            if (!context.scanReferences) {
              if (!property) {
                return {
                  valid: false,
                  reason: `Invalid argument "${
                    argNode.raw
                  }" in "${printExpression(node)}". Property ${
                    argNode.value
                  } does not appear on this record.`
                };
              }
              const result = argInfo.validate(property);
              if (!result) {
                return result;
              }
            }
            continue;
          }
          case 'choice-reference': {
            if (
              argNode.type !== 'Literal' ||
              typeof argNode.value !== 'number' ||
              !Number.isInteger(argNode.value)
            ) {
              return {
                valid: false,
                reason: `Invalid argument "${
                  argNode.raw
                }" in "${printExpression(node)}". Expected integer choice id.`
              };
            }

            if (!context.scanReferences) {
              if (!property) {
                throw new Error(
                  `Invalid function definition. Argument ${i} of ${callee.name} is a choice reference but there's no preceding property reference.`
                );
              }

              if (!property.choices?.some(c => c.choice_id === argNode.value)) {
                return {
                  valid: false,
                  reason: `Invalid choice id "${
                    argNode.raw
                  }" in "${printExpression(
                    node
                  )}". Valid choice IDs: ${property.choices
                    ?.map(x => x.choice_id)
                    .join(', ')}.`
                };
              }
            }
            continue;
          }
          case 'literal': {
            if (
              argNode.type === 'UnaryExpression' &&
              argNode.operator === '-' &&
              argNode.argument.type === 'Literal' &&
              typeof argNode.argument.value === 'number' &&
              Number.isFinite(argNode.argument.value)
            ) {
              const result = argInfo.validate(argNode.argument.value * -1);
              if (!result.valid) {
                return result;
              }
              continue;
            }
            if (argNode.type !== 'Literal') {
              return {
                valid: false,
                reason: `Invalid argument "${
                  argNode.raw
                }" in "${printExpression(node)}". Expected a literal value.`
              };
            }
            const result = argInfo.validate(argNode.value);
            if (!result.valid) {
              return result;
            }
            continue;
          }
          case 'value': {
            const result = validateActivityExpression({
              node: argNode,
              context
            });
            if (result.valid === false) {
              return result;
            }
            const result2 = argInfo.validate(
              result.type,
              valueTypes,
              i,
              node.arguments.length
            );
            if (!result2.valid && !context.scanReferences) {
              return result2;
            }
            valueTypes.push(result.type);
            continue;
          }
          case 'contextual-identifier': {
            if (argNode.type !== 'Identifier') {
              return {
                valid: false,
                reason: `Invalid argument "${
                  argNode.raw
                }" in "${printExpression(node)}". Expected an identifier.`
              };
            }
            const result = argInfo.validate(argNode.name);
            if (!result.valid) {
              return result;
            }
            continue;
          }
          default:
            throw new Error(
              `Unexpected argument type "${(argInfo as any).type}".`
            );
        }
      }
      return {
        valid: true,
        type: context.scanReferences
          ? 'int'
          : fn.returnType({
              context,
              args: node.arguments as CoreExpression[],
              valueTypes
            })
      };
    }
    case 'UnaryExpression': {
      const { operator, argument } = node;
      if (operator !== '-' && operator !== '!') {
        return {
          valid: false,
          reason: `Invalid expression "${printExpression(node)}".`
        };
      }
      const result = validateActivityExpression({
        node: argument as CoreExpression,
        context
      });
      if (result.valid === false) {
        return result;
      }
      if (operator === '-') {
        if (context.scanReferences) {
          return { valid: true, type: 'int' };
        }
        if (result.type !== 'decimal' && result.type !== 'int') {
          return {
            valid: false,
            reason: `Cannot negate "${printExpression(node)}" expression.`
          };
        }
        return { valid: true, type: result.type };
      } else {
        if (context.scanReferences) {
          return { valid: true, type: 'boolean' };
        }
        if (result.type !== 'boolean') {
          return {
            valid: false,
            reason: `Cannot negate "${printExpression(node)}" expression.`
          };
        }
        return { valid: true, type: 'boolean' };
      }
    }
    case 'Literal': {
      const { value, raw } = node;
      if (
        typeof value === 'number' &&
        isFinite(value) &&
        value >= -100000000 &&
        value <= 100000000
      ) {
        return { valid: true, type: value % 1 === 0 ? 'int' : 'decimal' };
      }
      if (typeof value === 'boolean') {
        return { valid: true, type: 'boolean' };
      }
      if (typeof value === 'string') {
        return { valid: true, type: 'string' };
      }
      return { valid: false, reason: `Invalid expression "${raw}".` };
    }
    case 'Identifier': {
      const name = node.name.toUpperCase();
      if (context.identifiers[name]) {
        return { valid: true, type: context.identifiers[name] };
      }
      return { valid: false, reason: `Invalid expression "${name}".` };
    }
    default:
      return { valid: false, reason: 'Invalid expression.' };
  }
}

function parseExpression(
  expression: string
): { valid: true; node: CoreExpression } | { valid: false; reason: string } {
  try {
    return { valid: true, node: jsep(expression) as CoreExpression };
  } catch (e) {
    if (
      e instanceof Error &&
      'description' in e &&
      typeof e.description === 'string' &&
      'index' in e &&
      typeof e.index === 'number'
    ) {
      return { valid: false, reason: e.description };
    }
    return { valid: false, reason: `Invalid expression "${expression}"` };
  }
}

export function parseAndValidateActivityExpression({
  expression,
  getContext
}: {
  expression: string;
  getContext: () => ActivityExpressionValidationContext;
}):
  | {
      valid: true;
      type: ExpressionReturnType;
      node: CoreExpression;
      originalNode: CoreExpression;
    }
  | { valid: false; reason: string } {
  const parseResult = parseExpression(expression);
  if (!parseResult.valid) {
    return parseResult;
  }
  const { node } = parseResult;
  const originalNode = structuredClone(node);
  const result = validateActivityExpression({
    node,
    context: { ...getContext() }
  });
  if (!result.valid) {
    return result;
  }
  return { valid: true, node, originalNode, type: result.type };
}
